import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "../../lib/api";
import { meetingTitle } from "../../lib/meetings-view";
import { clearCopy, newerOf, readCopy, writeCopy } from "../../lib/device-copy";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { SalesMeetingDetail } from "../../lib/sales-types";
import type { User } from "../../types";
import { MessageEditor, editorText } from "../editor/MessageEditor";
import { PersonalSettingsDialog } from "../PersonalSettingsDialog";
import { RecipientInput, type Recipient } from "../RecipientInput";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";

/**
 * Write and send a meeting's follow-up email.
 *
 * Pre-filled the way the rep would have filled it: the customer's side on To,
 * our own colleagues who were there on Cc, "Follow-up: <the meeting>" as the
 * subject. The body starts as a greeting, with the notes one press away --
 * "Insert notes" pastes them in to be edited down, because notes written for
 * ourselves are rarely what goes to a customer word for word -- and the desk's
 * saved replies are in the toolbar and on `#`, as in the helpdesk composer.
 *
 * It goes from the rep's own Gmail, never a shared channel. Somebody who has
 * not given Pulse permission to send is told how, before they write anything.
 */
export function FollowUpComposer({
  meeting,
  user,
  onClose,
}: {
  meeting: SalesMeetingDetail;
  user: User;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [settings, setSettings] = useState(false);

  const google = useQuery({
    queryKey: queryKeys.googleConnection,
    queryFn: api.googleConnection,
    staleTime: 60_000,
  });
  const canSend =
    google.data?.connected === true && google.data.capabilities.includes("send");

  /*
   * Where to start: your saved draft if there is one -- or a newer copy this
   * device kept because the draft could not reach the server -- and the
   * pre-filled email otherwise.
   */
  const copyKey = `pulse.meeting-follow-up.${meeting.id}.${user.id}`;
  const [initial] = useState<DraftShape>(() => {
    const server = meeting.followUpDraft
      ? {
          value: {
            to: meeting.followUpDraft.to,
            cc: meeting.followUpDraft.cc,
            subject: meeting.followUpDraft.subject,
            html: meeting.followUpDraft.bodyHtml,
          },
          at: meeting.followUpDraft.updatedAt,
        }
      : null;
    const chosen = newerOf(server, readCopy<DraftShape>(copyKey), (a, b) => JSON.stringify(a) === JSON.stringify(b));
    return chosen?.value ?? freshDraft(meeting, user);
  });
  const resumed = meeting.followUpDraft !== null || JSON.stringify(initial) !== JSON.stringify(freshDraft(meeting, user));

  const [to, setTo] = useState<Recipient[]>(initial.to);
  const [cc, setCc] = useState<Recipient[]>(initial.cc);
  const [subject, setSubject] = useState(initial.subject);
  const [html, setHtml] = useState(initial.html);
  const [draftState, setDraftState] = useState<"idle" | "saving" | "saved" | "offline">(
    resumed ? "saved" : "idle",
  );

  /*
   * The draft saves itself, like the notes: copied to the device on every
   * change and to the server a moment after typing stops, so it is there on
   * your laptop when you started it on your phone. Nothing is saved until
   * something is changed -- opening the composer and closing it again leaves
   * no draft behind.
   */
  const current: DraftShape = { to, cc, subject, html };
  const latest = useRef(current);
  latest.current = current;
  /* Whether there is a draft worth keeping: one resumed, or one changed. */
  const touched = useRef(resumed);
  const changed = useRef(false);
  const sent = useRef(false);

  const persist = (draft: DraftShape): Promise<void> => {
    setDraftState("saving");
    return salesApi
      .saveMeetingFollowUpDraft(meeting.id, draft)
      .then(() => {
        if (JSON.stringify(latest.current) === JSON.stringify(draft)) clearCopy(copyKey);
        setDraftState("saved");
        void client.invalidateQueries({ queryKey: queryKeys.salesMeeting(meeting.id) });
      })
      .catch(() => setDraftState("offline"));
  };

  const serialised = JSON.stringify(current);
  useEffect(() => {
    /* Compared with where it started rather than skipping the first run, which
       StrictMode runs twice. */
    if (!changed.current && serialised === JSON.stringify(initial)) return;
    changed.current = true;
    touched.current = true;
    writeCopy(copyKey, latest.current);
    const timer = window.setTimeout(() => void persist(latest.current), 1_500);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialised]);

  /* No signal: try again every ten seconds while the composer is open. */
  useEffect(() => {
    if (draftState !== "offline") return;
    const timer = window.setTimeout(() => void persist(latest.current), 10_000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftState]);

  /* Closing keeps the draft: one last save of whatever was typed since. */
  const close = (): void => {
    if (touched.current && !sent.current) void persist(latest.current);
    onClose();
  };

  const discard = useMutation({
    mutationFn: () => salesApi.discardMeetingFollowUpDraft(meeting.id),
    onSettled: () => {
      clearCopy(copyKey);
      void client.invalidateQueries({ queryKey: queryKeys.salesMeeting(meeting.id) });
      onClose();
    },
  });

  const send = useMutation({
    mutationFn: () =>
      salesApi.sendMeetingFollowUp(meeting.id, {
        to,
        cc: cc.length > 0 ? cc : undefined,
        subject,
        html,
      }),
    onSuccess: (detail) => {
      /* The server drops the draft when it sends; the device's copy goes too. */
      sent.current = true;
      clearCopy(copyKey);
      client.setQueryData(queryKeys.salesMeeting(meeting.id), detail);
      void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
      onClose();
    },
  });

  const empty = !editorText(html).trim();

  return (
    <Overlay
      title="Follow-up email"
      size="lg"
      onClose={close}
      footer={
        <>
          {send.isError ? (
            <p className="mr-auto text-xs text-red-600">
              {send.error instanceof ApiError ? send.error.message : "That did not send."}
            </p>
          ) : (
            <p className="mr-auto text-[11px] text-faint">
              {draftState === "saving"
                ? "Saving draft… "
                : draftState === "saved"
                  ? "Draft saved. "
                  : draftState === "offline"
                    ? "No connection — draft kept on this device. "
                    : ""}
              {google.data?.googleEmail ? `From ${google.data.googleEmail}. ` : ""}
              Logged on {meeting.crmCompanyName ?? meeting.companyName}
              {meeting.companyId ? "'s timeline." : " — not a CRM company, so only on this meeting."}
            </p>
          )}
          {/* Discard throws the draft away; the cross keeps it for later. */}
          <Button
            variant="secondary"
            disabled={discard.isPending}
            onClick={() => (touched.current ? discard.mutate() : onClose())}
          >
            Discard
          </Button>
          <Button
            disabled={!canSend || to.length === 0 || empty || send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? "Sending…" : "Send"}
          </Button>
        </>
      }
    >
      {google.data && !canSend ? (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          Follow-ups go from your own Gmail, and Pulse is not allowed to send
          from it yet.{" "}
          <button
            type="button"
            onClick={() => setSettings(true)}
            className="font-medium underline"
          >
            Open Your settings
          </button>
          , tick <strong>Send email</strong> under Google and reconnect.
        </p>
      ) : null}

      {settings ? (
        <PersonalSettingsDialog user={user} onClose={() => setSettings(false)} />
      ) : null}

      <div className="space-y-2">
        <Row label="To">
          <RecipientInput value={to} onChange={setTo} placeholder="Who was there from their side" />
        </Row>
        <Row label="Cc">
          <RecipientInput value={cc} onChange={setCc} placeholder="Add a colleague" />
        </Row>
        <Row label="Subject">
          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            className="h-8 w-full rounded-md border border-line bg-surface px-2.5 text-sm text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          />
        </Row>

        <MessageEditor
          initialHtml={html}
          onChange={setHtml}
          uploads={[]}
          onUploadsChange={() => undefined}
          onError={() => undefined}
          allowAttachments={false}
          placeholder="Write your follow-up…  #shortcut inserts a saved reply"
          surfaceHeight="min-h-48 max-h-[50vh]"
          onSubmit={() => {
            if (canSend && to.length > 0 && !empty) send.mutate();
          }}
          extraTools={(tools) =>
            meeting.notesHtml ? (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => tools.insertHtml(meeting.notesHtml!)}
                title="Paste the meeting notes in, to edit down"
                className="flex h-7 items-center rounded px-2 text-xs text-muted hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
              >
                Insert notes
              </button>
            ) : null
          }
        />
      </div>
    </Overlay>
  );
}

interface DraftShape {
  to: Recipient[];
  cc: Recipient[];
  subject: string;
  html: string;
}

/**
 * The pre-filled follow-up: the customer's side on To, our colleagues who
 * were there on Cc, "Follow-up: <meeting>" and a greeting.
 */
function freshDraft(meeting: SalesMeetingDetail, user: User): DraftShape {
  const to = meeting.guests.map((guest) => ({ email: guest.email, name: guest.name ?? undefined }));
  const firstName = (to[0]?.name ?? meeting.contactName ?? "").split(" ")[0];
  return {
    to,
    cc: meeting.attendees
      .filter((person) => person.userId !== user.id && person.email)
      .map((person) => ({ email: person.email!, name: person.name ?? undefined })),
    subject: `Follow-up: ${meetingTitle(meeting)}`,
    html: `<p>Hi${firstName ? ` ${escapeText(firstName)}` : ""},</p><p>Thanks for your time${sameDayAsToday(meeting.startAt) ? " today" : ""}.</p><p><br></p>`,
  };
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-14 shrink-0 pt-1.5 text-xs text-faint">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function sameDayAsToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

function escapeText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
